import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "./config";
import PushNotificationSettings from "./components/PushNotificationSettings";
import PremiumBadge from "./components/PremiumBadge";
import usePremiumPlan from "./hooks/usePremiumPlan";
import "./styles/Profile.css";

function Profile({ user, onLogout, onUserUpdate }) {
  const navigate = useNavigate();
  const isPremium = usePremiumPlan(user?.id);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newUsername, setNewUsername] = useState(user?.username || "");
  const [newBio, setNewBio] = useState(user?.bio || "");
  const [newProfileImage, setNewProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    user?.profileImage ? `${config.apiUrl}${user.profileImage}` : null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  // Sync state when user prop changes
  useEffect(() => {
    if (user) {
      setNewUsername(user.username);
      setNewBio(user.bio || "");
      setImagePreview(
        user.profileImage ? `${config.apiUrl}${user.profileImage}` : null
      );
    }
  }, [user]);

  // Fetch fresh user profile data on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user?.id) {
        try {
          const res = await axios.get(
            `${config.apiUrl}/api/auth/profile/${user.id}?requestingUserId=${user.id}`
          );

          // Update the user data with fresh profile info
          const updatedUser = {
            ...user,
            bio: res.data.bio || "",
            profileImage: res.data.profileImage,
            username: res.data.username,
            email: res.data.email,
          };

          // Update localStorage
          localStorage.setItem("chatapp_user", JSON.stringify(updatedUser));

          // Update parent component
          if (onUserUpdate) {
            onUserUpdate(updatedUser);
          }

          // Update local state
          setNewBio(res.data.bio || "");
        } catch (error) {
          console.error("Failed to fetch user profile:", error);
        }
      }
    };

    fetchUserProfile();
  }, []); // Only run on mount

  const handleLogoutClick = () => {
    setShowModal(true);
  };

  const handleConfirm = () => {
    setShowModal(false);
    onLogout();
    navigate("/login");
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setNewUsername(user?.username || "");
    setNewBio(user?.bio || "");
    setImagePreview(
      user?.profileImage ? `${config.apiUrl}${user.profileImage}` : null
    );
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewUsername(user?.username || "");
    setNewBio(user?.bio || "");
    setNewProfileImage(null);
    setImagePreview(
      user?.profileImage ? `${config.apiUrl}${user.profileImage}` : null
    );
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveChanges = async () => {
    if (!newUsername.trim()) {
      alert("Username cannot be empty");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("username", newUsername);
      formData.append("bio", newBio || ""); // Ensure bio is never undefined
      formData.append("email", user?.email || ""); // Use existing email, not editable
      formData.append("currentUsername", user?.username || "");
      if (newProfileImage) {
        formData.append("profileImage", newProfileImage);
      }

      const res = await axios.put(
        `${config.apiUrl}/api/auth/profile/${user.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Update user data
      const updatedUser = {
        ...user,
        username: res.data.user.username,
        email: res.data.user.email,
        bio: res.data.user.bio || "",
        profileImage: res.data.user.profileImage,
      };

      // Update localStorage
      localStorage.setItem("chatapp_user", JSON.stringify(updatedUser));

      // Notify parent component
      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }

      setIsEditing(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Profile update error:", error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert("Failed to update profile. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };


  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const d = new Date(dateString);
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };


  // Guard against undefined user
  if (!user) {
    return (
      <div className="profile-container">
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <button className="back-btn" onClick={() => navigate('/')} aria-label="Back" style={{ position: 'absolute', left: 20, top: 20, background: 'none', border: 'none', cursor: 'pointer' }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="#075e54"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" /></svg>
      </button>
      <h2>My Profile</h2>

      {/* Profile Image Section */}
      <div className="profile-image-container">
        <div className="profile-image-wrap">
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="Profile"
              className="profile-image"
              onClick={() => setShowImageModal(true)}
              style={{ cursor: 'pointer' }}
              title="Click to view full image"
            />
          ) : (
            <div
              className="profile-image-placeholder notranslate"
              onClick={() => setShowImageModal(true)}
              style={{ cursor: 'pointer' }}
              title="Click to view profile"
            >
              {user?.username?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          )}
          {isPremium && <PremiumBadge size="lg" />}
        </div>

        {/* Edit Icon on Profile Image */}
        <button 
          className="profile-edit-icon" 
          onClick={handleEditClick}
          title="Edit Profile"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>

        {isEditing && (
          <div className="image-upload-container">
            <label htmlFor="profile-image-edit" className="image-upload-label">
              Change Profile Image
            </label>
            <input
              id="profile-image-edit"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: "none" }}
            />
          </div>
        )}
      </div>

      {/* Profile Info Section */}
      <div className="profile-info">
        {isEditing ? (
          <div className="edit-form">
            <div className="form-group">
              <label>
                <b>Username:</b>
              </label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                className="edit-input"
                placeholder="Enter new username"
              />
            </div>
            <div className="form-group">
              <label>
                <b>Bio:</b>
              </label>
              <textarea
                value={newBio}
                onChange={(e) => setNewBio(e.target.value)}
                className="edit-input"
                placeholder="Tell us about yourself..."
                maxLength="150"
                rows="3"
                style={{
                  resize: "vertical",
                  minHeight: "60px",
                  fontFamily: "inherit",
                }}
              />
              <div className="char-count">{newBio.length}/150 characters</div>
            </div>
            <div className="form-group">
              <label>
                <b>Email:</b>
              </label>
              <div className="readonly-field">
                {user.email || "Not provided"}
              </div>
            </div>
            {/* <div><b>User ID:</b> {user.id}</div> */}
          </div>
        ) : (
          <>
            <div>
              <b>Username:</b>{" "}
              <span className="notranslate">{user?.username || "Not available"}</span>
            </div>
            <div>
              <b>Bio:</b> {user.bio || newBio || "No bio added yet"}
            </div>
            <div>
              <b>Email:</b> {user.email || "Not provided"}
            </div>
            {/* <div><b>User ID:</b> {user.id}</div> */}
          </>
        )}
      </div>

      {/* Action Buttons */}
      {isEditing && (
        <div className="profile-actions">
          <button
            className="save-btn"
            onClick={handleSaveChanges}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </button>
          <button className="cancel-btn" onClick={handleCancelEdit}>
            Cancel
          </button>
        </div>
      )}

      {/* Push Notification Settings */}
      <PushNotificationSettings user={user} />


      {/* Logout Button at the End */}
      {!isEditing && (
        <div className="profile-logout-section">
          <button className="logout-btn-bottom" onClick={handleLogoutClick}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Logout
          </button>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-message">
              Are you sure you want to logout?
            </div>
            <div className="modal-actions">
              <button className="modal-btn confirm" onClick={handleConfirm}>
                Yes, Logout
              </button>
              <button className="modal-btn cancel" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-message">Profile updated successfully!</div>
            <div className="modal-actions">
              <button
                className="modal-btn confirm"
                onClick={() => setShowSuccessModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}


      {showImageModal && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="image-modal-close"
              onClick={() => setShowImageModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Profile"
                className="image-modal-img"
              />
            ) : (
              <div className="image-modal-placeholder notranslate">
                {user?.username?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
